import { useQuery, type QueryHookOptions } from "@apollo/client";
import { useMemo } from "react";
import { DOMAIN, PERMISSION, useHasCapabilityBool } from "@/hooks/useCapabilities";
import { Person } from "@/types";
import { SOME_PEOPLE_CAREER_QUERY, SOME_PEOPLE_QUERY, type PeopleQueryVariables } from "../queries";

type SomePeopleData = {
  people?: {
    totalCount?: number;
    nodes?: Person[];
  };
};

export function useSomePeopleQuery(
  options: QueryHookOptions<SomePeopleData, PeopleQueryVariables> & { variables?: PeopleQueryVariables } = {}
) {
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const { skip, variables, ...rest } = options;

  const { data, loading, error, ...queryRest } = useQuery<SomePeopleData, PeopleQueryVariables>(SOME_PEOPLE_QUERY, {
    ...rest,
    variables,
    skip
  });

  const { data: careerData, loading: careerLoading } = useQuery<SomePeopleData, PeopleQueryVariables>(
    SOME_PEOPLE_CAREER_QUERY,
    {
      variables,
      skip: Boolean(skip) || !canViewPersonCareer,
      fetchPolicy: "network-only"
    }
  );

  const mergedData = useMemo(() => {
    if (!data?.people?.nodes) return data;

    const careerByKey = new Map<string, Person>();
    careerData?.people?.nodes?.forEach((person) => {
      const key = person.pgaId ?? person.id;
      if (key) careerByKey.set(key, person);
    });

    return {
      ...data,
      people: {
        ...data.people,
        nodes: data.people.nodes.map((person) => {
          const key = person.pgaId ?? person.id;
          const careerFields = key ? careerByKey.get(key) : undefined;
          if (!careerFields) return person;
          return {
            ...person,
            primaryFacility: careerFields.primaryFacility,
            employments: careerFields.employments
          };
        })
      }
    };
  }, [careerData?.people?.nodes, data]);

  return {
    data: mergedData,
    loading: loading || (canViewPersonCareer && careerLoading),
    error,
    ...queryRest
  };
}
